import React, { useEffect, useState } from "react";
import useFetch from "hooks/useFetch";
import {
  Button,
  EmptyState,
  Icon,
  IconButton,
  PageHeader,
  Pagination,
  styles as ui,
  useToast,
} from "stories/Admin";
import styles from "./MawaqeetLocationsPage.module.scss";

interface Location {
  id: number;
  name: string;
  lat: number;
  lon: number;
  county: string;
  municipality: string;
  prayerTimesTables: {
    id: number;
    name: string;
  }[];
}

const MawaqeetLocationsPage: React.FC = () => {
  const { execute } = useFetch();
  const toast = useToast();
  const [locations, setLocations] = useState<Location[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<Location[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(
    null,
  );
  const itemsPerPage = 10;
  const prayerTimeTableId = 2;

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = (await execute(
          "/api/v1/locations",
          "GET",
        )) as Location[];
        if (response) {
          const filtered = response.filter(
            (location) =>
              !location.prayerTimesTables.some(
                (table) => table.id === prayerTimeTableId,
              ),
          );
          setLocations(filtered);
        }
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    };

    fetchLocations();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      const filtered = locations.filter((location) =>
        location.name.toLowerCase().includes(lowerSearchTerm),
      );
      setFilteredLocations(filtered);
    } else {
      setFilteredLocations(locations);
    }
    setCurrentPage(1);
  }, [searchTerm, locations]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredLocations.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const handleAddPrayerTimeTableClick = (location: Location) => {
    setSelectedLocation(location);
    setShowConfirmationModal(true);
  };

  const handleConfirmAddPrayerTimeTable = async () => {
    if (!selectedLocation) return;

    try {
      const payload = { locationId: selectedLocation.id, prayerTimeTableId };
      await execute(
        "/api/v1/prayer-times-tables-with-location",
        "POST",
        payload,
      );
      toast.success(
        `Mawaqeet-tabellen er lagt til for ${selectedLocation.name}.`,
      );
      setLocations((prev) =>
        prev.filter((location) => location.id !== selectedLocation.id),
      );
      setShowConfirmationModal(false);
      setSelectedLocation(null);
    } catch (error) {
      console.error("Error adding Mawaqeet prayer time table:", error);
      toast.error("Kunne ikke legge til Mawaqeet-tabellen.");
    }
  };

  const totalPages = Math.ceil(filteredLocations.length / itemsPerPage);
  const closeModal = () => setShowConfirmationModal(false);

  return (
    <>
      <PageHeader
        title="Lokasjoner uten Mawaqeet"
        subtitle="Byer som ennå ikke har Mawaqeet-tabellen. Legg til tabellen for å kunne publisere tider der."
      />

      <div className={styles.searchRow}>
        <div className={styles.search}>
          <Icon name="search" size={18} strokeWidth={2} />
          <input
            type="text"
            className={ui.inp}
            placeholder="Søk etter lokasjon …"
            aria-label="Søk etter lokasjon"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <span className={ui.help}>
          {filteredLocations.length}{" "}
          {filteredLocations.length === 1 ? "lokasjon" : "lokasjoner"}
        </span>
      </div>

      {currentItems.length > 0 ? (
        <div className={styles.grid}>
          {currentItems.map((location) => (
            <div key={location.id} className={styles.locCard}>
              <span className={styles.locHead}>
                <span className={styles.pin} aria-hidden="true">
                  <Icon name="pin" size={19} strokeWidth={2} />
                </span>
                <span className={styles.locName}>
                  <b>{location.name}</b>
                  <span className={ui.help}>
                    {location.county} · {location.municipality}
                  </span>
                </span>
              </span>
              <span className={`${ui.help} ${ui.num} ${styles.coords}`}>
                <GlobeIcon />
                {location.lat}, {location.lon}
              </span>
              <Button
                variant="secondary"
                size="sm"
                icon={<Icon name="plus" size={16} strokeWidth={2} />}
                onClick={() => handleAddPrayerTimeTableClick(location)}
              >
                Legg til Mawaqeet-tabell
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          icon="pin"
          title={
            searchTerm
              ? "Ingen lokasjoner funnet"
              : "Ingen lokasjoner tilgjengelige"
          }
        >
          {searchTerm
            ? "Prøv et annet søkeord."
            : "Alle lokasjoner har allerede Mawaqeet-tabellen."}
        </EmptyState>
      )}

      {filteredLocations.length > itemsPerPage && (
        <Pagination
          page={currentPage}
          pageCount={totalPages}
          onChange={paginate}
        />
      )}

      {showConfirmationModal && (
        <div
          className={ui.overlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div
            className={`${ui.modal} ${styles.modal}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="addTableTitle"
            onKeyDown={(e) => {
              if (e.key === "Escape") closeModal();
            }}
          >
            <h2 className={ui.mh} id="addTableTitle">
              <span className={ui.mhIcon} aria-hidden="true">
                <Icon name="table" size={18} strokeWidth={2} />
              </span>
              Legg til Mawaqeet-tabell?
              <IconButton
                label="Lukk"
                className={styles.close}
                icon={<Icon name="x" size={18} strokeWidth={2} />}
                onClick={closeModal}
              />
            </h2>
            <div className={ui.mb}>
              {selectedLocation ? (
                <span className={styles.modalText}>
                  Mawaqeet-tabellen legges til for{" "}
                  <b>{selectedLocation.name}</b>. Byen forsvinner fra denne
                  listen og blir valgbar under Bønnetider.
                </span>
              ) : (
                <span>Noe gikk galt. Prøv igjen senere.</span>
              )}
            </div>
            <div className={ui.mf}>
              <Button variant="secondary" onClick={closeModal}>
                Avbryt
              </Button>
              <Button
                autoFocus
                icon={<Icon name="check" size={16} strokeWidth={2} />}
                onClick={handleConfirmAddPrayerTimeTable}
              >
                Legg til
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const GlobeIcon: React.FC = () => (
  <svg
    className={ui.icon}
    viewBox="0 0 24 24"
    width={13}
    height={13}
    strokeWidth={2}
    aria-hidden="true"
  >
    <path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM3.5 9h17M3.5 15h17M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z" />
  </svg>
);

export default MawaqeetLocationsPage;
